// Screener → ChartDetailView navigation context.
//
// The Screener filters client-side over the whole universe (screenerFilters
// registry); its results are NOT expressible as the legacy server-side
// `filters` object that IndexesPage/SectorsPage pass through location.state.
// Instead the Screener ships its own filter/sort state along to the detail
// page, which rebuilds the exact same ordered result list with the same
// registry functions. Next/Previous then walk the filtered set, and "Back
// to Search Results" returns the state to /screener so the filter survives.
import ApiService from '../services/ApiService';
import {
  filterStocks,
  normalizeStock,
  resolveSort,
  sortStocks,
  validateSavedState,
} from './screenerFilters';

/**
 * Build the /chart/:id navigation state for a Screener entry point.
 * Returns null when filterState no longer validates against the registry
 * (e.g. history.state left by an older deploy) so the caller falls back to
 * the legacy context instead of navigating with a silently-wrong filter.
 */
export function buildScreenerNavState({ filterState, sort, chartOnly } = {}) {
  const { state, errors } = validateSavedState(filterState);
  if (errors.length) return null;
  return {
    filterState: state,
    sort: resolveSort(sort),
    chartOnly: Boolean(chartOnly),
  };
}

/** Validate a navigation state received via location.state. Null if unusable. */
export function parseScreenerNavState(state) {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
  return buildScreenerNavState(state);
}

// One ordered list per filter/sort combination, cached in-module so hopping
// Next/Previous between detail pages does not refetch the universe.
let listCache = { key: null, list: null };

const listKey = (nav) => JSON.stringify([nav.filterState, nav.sort, nav.chartOnly]);

/**
 * The ordered Screener result list — same filter + sort as the grid — as
 * [{ id, ticker }]. Uses the same normalizeStock/filterStocks/sortStocks
 * pipeline as ScreenerPage so inclusion and order cannot drift.
 */
export async function getScreenerOrderedList(nav) {
  const key = listKey(nav);
  if (listCache.key === key && listCache.list) return listCache.list;
  const result = await ApiService.getScreenerUniverse();
  const universe = (result.data || []).map(normalizeStock).filter((s) => s.ticker);
  let stocks = sortStocks(filterStocks(universe, nav.filterState), nav.sort);
  if (nav.chartOnly) stocks = stocks.filter((s) => s.has_chart);
  listCache = { key, list: stocks.map((s) => ({ id: s.id, ticker: s.ticker })) };
  return listCache.list;
}

/** Test hook: drop the cached list so the next call refetches. */
export function __resetScreenerListCache() {
  listCache = { key: null, list: null };
}